import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { forkJoin, map, switchMap } from 'rxjs';
import { listarFormulasContexto } from '@shared/components/formula/utils/formula-contexto.util';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { DataSourceOrdenacaoInsensivel } from '@shared/helpers/ordenacao-tabela.helper';
import { ModuloEnum } from '@shared/enums/usuario/modulo.enum';
import { FuncionalidadeEnum } from '@shared/enums/usuario/funcionalidade.enum';
import { RegiaoGeograficaEnum } from '@shared/enums/usuario/regiao-geografica.enum';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { UtilService } from '@shared/services/util.service';
import { DifalService } from '@shared/services/apis/motor-fiscal/difal.service';
import { DifalSeedRequest } from '@shared/services/requests/motor-fiscal/difal-seed.request';
import { RegiaoGeograficaItem, UfOption, UnidadeFederativaService } from '@shared/services/apis/usuario/unidade-federativa.service';
import { DifalListStruct } from '@shared/services/structs/motor-fiscal/difal-list.struct';
import { DifalErrorCodeEnum } from '@shared/enums/motor-fiscal/difal-error-code.enum';
import { EmptyListComponent } from '@shared/components/empty-list/empty-list.component';
import { LoadingBoxDefaultListComponent } from '@shared/components/loading-box-default-list/loading-box-default-list.component';
import { TipoFormulaEnum } from '@shared/enums/formula/tipo-formula.enum';
import { FormulaCatalogoService } from '@shared/services/apis/motor-fiscal/formula-catalogo.service';
import { OrigemFormulaOption } from './origem-formula-option';
import { DifalListViewStruct } from './difal-list-view.struct';

export { DifalListViewStruct } from './difal-list-view.struct';

const CHAVE_VARIAVEL_BASE_CALCULO_DIFAL = 'nfe.infnfe.det.imposto.icmsufdest.vbcufdest';

@Component({
    selector: 'app-difais-lista',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        MatBadgeModule,
        MatButtonModule,
        MatCheckboxModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatMenuModule,
        MatProgressSpinnerModule,
        MatSelectModule,
        MatSortModule,
        MatTableModule,
        EmptyListComponent,
        LoadingBoxDefaultListComponent,
    ],
    templateUrl: './difais-lista.component.html',
    styleUrl: './difais-lista.component.css',
})
export class DifaisListaComponent implements OnInit, OnDestroy {
    public moduloEnum = ModuloEnum.Configuracoes;
    public funcionalidadeEnum = FuncionalidadeEnum.Configuracoes_NotasFiscais;

    public model: FormGroup;

    public _paginaAtual: number = 1;
    public _tamanhoPagina: number = 100;
    public _totalRegistros: number = 0;
    public _possuiProximaPagina: boolean = false;

    public _dataSource = new DataSourceOrdenacaoInsensivel<DifalListViewStruct>();
    public _allData: DifalListViewStruct[] = [];
    public _isLoading: boolean = false;

    public displayedColumns: string[] = [
        'uf', 'pIcmsUfDest', 'pIcmsInter', 'pFcpUfDest', 'reducaoBc', 'origemFormula', 'actions'
    ];

    public regiaoOptions = [
        { value: '', label: 'Todas' },
    ];

    public origemOptions: OrigemFormulaOption[] = [
        { id: '', tipoFormula: null, modeloFormula: null, label: 'Todas' },
        { id: 'personalizada', tipoFormula: TipoFormulaEnum.Personalizada, modeloFormula: null, label: 'Personalizadas' },
    ];

    @ViewChild(MatSort) set matSort(sort: MatSort) {
        this._dataSource.sort = sort;
    }

    constructor(
        private fb: FormBuilder,
        private router: Router,
        private difalService: DifalService,
        private formulaService: FormulaCatalogoService,
        private unidadeFederativaService: UnidadeFederativaService,
        private alertService: AlertService,
        private utilService: UtilService,
    ) { }

    ngOnInit(): void {
        this.model = this.fb.group({
            pesquisa: [''],
            regiao: [''],
            origem: [''],
            somenteReducaoBc: [false],
        });
        this.loadFilters();
        this.carregarDifais(true);
    }

    ngOnDestroy(): void {
        this.utilService.salvarFiltro(this.moduloEnum, this.funcionalidadeEnum, this.model, []);
    }

    private loadFilters(): void {
        const saved = this.utilService.obterFiltro(this.moduloEnum, this.funcionalidadeEnum);
        if (saved) this.model.patchValue(saved);
    }

    public carregarDifais(reset: boolean = false): void {
        this._isLoading = true;
        const pesquisa = this.model.get('pesquisa')?.value || null;
        const regiao = this.model.get('regiao')?.value || null;
        const origemId = this.model.get('origem')?.value || '';
        const somenteReducaoBc = this.model.get('somenteReducaoBc')?.value || null;

        forkJoin({
            catalogo: this.formulaService.getContexto('difal'),
            ufs: this.unidadeFederativaService.getUnidadesFederativas(),
            regioes: this.unidadeFederativaService.getRegioesGeograficas(),
        }).pipe(switchMap(dados => {
            if (dados.catalogo.isError) throw new Error(dados.catalogo.errorDescription || 'Não foi possível carregar os modelos.');
            this.atualizarOpcoesOrigem(dados.catalogo.grupos ?? []);
            const origem = this.origemOptions.find(item => item.id === origemId) ?? this.origemOptions[0];
            return this.difalService.getAll(null, origem.tipoFormula, origem.modeloFormula, somenteReducaoBc, this._paginaAtual, this._tamanhoPagina)
                .pipe(map(difal => ({ ...dados, difal })));
        })).subscribe({
            next: ({ difal, catalogo, ufs, regioes }) => {
                if (difal.isError) {
                    // Empresa ainda não inicializou a matriz: dispara o seed com as UFs da API Usuario.
                    if (difal.errorCode === DifalErrorCodeEnum.SeedPendente) {
                        this.inicializarMatriz(ufs);
                        return;
                    }
                    this._isLoading = false;
                    this.alertService.show('Erro', difal.errorDescription || 'Erro ao carregar DIFALs', AlertType.error);
                    return;
                }

                this._isLoading = false;
                this.atualizarOpcoesOrigem(catalogo.grupos ?? []);
                this.renderizarLista(difal.difais, ufs, regioes, pesquisa, regiao, difal.totalRegistros, difal.possuiProximaPagina, reset);
            },
            error: err => {
                this._isLoading = false;
                this.alertService.show('Erro inesperado', err.message || 'Erro ao carregar DIFALs', AlertType.error);
            },
        });
    }

    /** Inicializa a matriz DIFAL da empresa enviando as UFs já carregadas da API Usuario. */
    private inicializarMatriz(ufs: UfOption[]): void {
        const request: DifalSeedRequest = {
            unidadesFederativas: ufs.map(uf => ({ idUnidadeFederativa: uf.idUnidadeFederativa, uf: uf.id })),
        };

        this.alertService.show('Informação', 'Carregando as configurações base...', AlertType.info);
        this.difalService.seed(request).subscribe({
            next: seed => {
                if (seed.isError) {
                    this._isLoading = false;
                    this.alertService.show('Erro', seed.errorDescription || 'Erro ao inicializar a matriz DIFAL', AlertType.error);
                    return;
                }

                this._paginaAtual = 1;
                this.carregarDifais(true);
            },
            error: err => {
                this._isLoading = false;
                this.alertService.show('Erro inesperado', err.message || 'Erro ao inicializar a matriz DIFAL', AlertType.error);
            },
        });
    }

    private renderizarLista(
        difais: DifalListStruct[],
        ufs: UfOption[],
        regioes: RegiaoGeograficaItem[],
        pesquisa: string | null,
        regiao: string | null,
        totalRegistros: number,
        possuiProximaPagina: boolean,
        reset: boolean
    ): void {
        this.regiaoOptions = [
            { value: '', label: 'Todas' },
            ...regioes.map(r => ({ value: r.descricao, label: r.descricao })),
        ];

        const dados = this.enriquecerComUnidadeFederativa(difais, ufs, regioes);
        const filtrados = this.filtrarDadosUsuario(dados, pesquisa, regiao);

        this._allData = reset ? filtrados : [...this._allData, ...filtrados];
        this._totalRegistros = totalRegistros;
        this._possuiProximaPagina = possuiProximaPagina;
        this._dataSource.data = this._allData;
    }

    private enriquecerComUnidadeFederativa(
        difais: DifalListStruct[],
        ufs: UfOption[],
        regioes: RegiaoGeograficaItem[]
    ): DifalListViewStruct[] {
        const ufsById = new Map(ufs.map(uf => [uf.idUnidadeFederativa, uf]));
        const regioesById = new Map(regioes.map(regiao => [regiao.idRegiaoGeografica, regiao.descricao]));

        return difais.map(difal => {
            const uf = ufsById.get(difal.idUnidadeFederativa);
            const regiao = uf?.idRegiaoGeografica ? regioesById.get(uf.idRegiaoGeografica) ?? '—' : '—';
            return {
                ...difal,
                siglaUf: uf?.id ?? difal.idUnidadeFederativa.toString(),
                nomeUf: uf?.descricao ?? 'UF não encontrada',
                regiao,
                regiaoEnum: this.resolverRegiao(regiao),
            };
        });
    }

    private filtrarDadosUsuario(
        dados: DifalListViewStruct[],
        pesquisa: string | null,
        regiao: string | null
    ): DifalListViewStruct[] {
        let filtrados = dados;

        if (pesquisa) {
            const termo = pesquisa.trim().toLowerCase();
            filtrados = filtrados.filter(item =>
                item.siglaUf.toLowerCase().includes(termo) ||
                item.nomeUf.toLowerCase().includes(termo)
            );
        }

        if (regiao) {
            filtrados = filtrados.filter(item => item.regiao.toLowerCase() === regiao.toLowerCase());
        }

        return filtrados;
    }

    public get qtdFiltrosAvancados(): number {
        let qtd = 0;
        if (this.model?.get('regiao')?.value) qtd++;
        if (this.model?.get('origem')?.value) qtd++;
        if (this.model?.get('somenteReducaoBc')?.value) qtd++;
        return qtd;
    }

    public clickPesquisar(): void {
        this._paginaAtual = 1;
        this._allData = [];
        this._dataSource.data = [];
        this.carregarDifais(true);
    }

    public aplicarFiltros(): void {
        this.clickPesquisar();
    }

    public limparFiltros(): void {
        this.model.reset({ pesquisa: '', regiao: '', origem: '', somenteReducaoBc: false });
        this.clickPesquisar();
    }

    public proximaPagina(): void {
        this._paginaAtual++;
        this.carregarDifais();
    }

    public formatarPercentual(valor: number | null | undefined): string {
        const numero = valor ?? 0;
        return `${numero.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}%`;
    }

    public rotuloOrigem(item: DifalListStruct): string {
        if (item.tipoFormula === TipoFormulaEnum.Personalizada) return 'Personalizada';
        const letra = item.modeloFormula ? `Modelo ${item.modeloFormula}` : 'Modelo';
        return item.nomeModeloFormula ? `${letra} - ${item.nomeModeloFormula}` : letra;
    }

    public classeOrigem(item: DifalListStruct): string {
        if (item.tipoFormula === TipoFormulaEnum.Personalizada) return 'badge-vinho';
        if (item.tipoFormula === TipoFormulaEnum.ModeloAlternativo) return 'badge-roxo';
        return 'badge-teal';
    }

    private atualizarOpcoesOrigem(grupos: import('../../../../../../shared/services/structs/formula/formula-catalogo-grupo.struct').FormulaCatalogoGrupoStruct[]): void {
        const modelos = grupos
            .flatMap(listarFormulasContexto)
            .find(formula => formula.variavelPrincipal?.chave === CHAVE_VARIAVEL_BASE_CALCULO_DIFAL)
            ?.modelos ?? [];
        const opcoesModelo = modelos.map(modelo => ({
            id: `modelo:${modelo.modelo}`,
            tipoFormula: modelo.isModeloPadraoSistema ? TipoFormulaEnum.ModeloPadrao : TipoFormulaEnum.ModeloAlternativo,
            modeloFormula: modelo.modelo,
            label: modelo.nome ? `Modelo ${modelo.modelo} - ${modelo.nome}` : `Modelo ${modelo.modelo}`,
        }));
        this.origemOptions = [
            { id: '', tipoFormula: null, modeloFormula: null, label: 'Todas' },
            ...opcoesModelo,
            { id: 'personalizada', tipoFormula: TipoFormulaEnum.Personalizada, modeloFormula: null, label: 'Personalizadas' },
        ];
    }

    /** Resolve a região (nome vindo da API) para o enum tipado. Fronteira: única conversão por nome. */
    private resolverRegiao(descricao: string): RegiaoGeograficaEnum | null {
        const nome = (descricao ?? '').trim().toLowerCase();
        switch (nome) {
            case 'norte': return RegiaoGeograficaEnum.Norte;
            case 'nordeste': return RegiaoGeograficaEnum.Nordeste;
            case 'centro-oeste':
            case 'centro oeste': return RegiaoGeograficaEnum.CentroOeste;
            case 'sudeste': return RegiaoGeograficaEnum.Sudeste;
            case 'sul': return RegiaoGeograficaEnum.Sul;
            default: return null;
        }
    }

    /** Cor do badge por região (paleta global), espelhando o protótipo. */
    public corBadgeRegiao(regiao: RegiaoGeograficaEnum | null): string {
        switch (regiao) {
            case RegiaoGeograficaEnum.Norte: return 'badge-verde';
            case RegiaoGeograficaEnum.Nordeste: return 'badge-laranja';
            case RegiaoGeograficaEnum.CentroOeste: return 'badge-lima';
            case RegiaoGeograficaEnum.Sudeste: return 'badge-azul';
            case RegiaoGeograficaEnum.Sul: return 'badge-roxo';
            default: return 'badge-cinza';
        }
    }

    public editar(item: DifalListViewStruct): void {
        this.router.navigate(['/configuracoes/notas-fiscais/tabela-difal/cadastrar', item.idDifal]);
    }
}
